import { computed } from 'mobx';
import { observer } from 'mobx-react';
import { autoinject, bind, HelpLink, inject, ModalHandle, SocketIcon, SubTopBar, Tooltip } from 'Mri.Web.Common';
import * as React from 'react';
import { LinkContainer } from 'react-router-bootstrap';
import { Link } from 'react-router-dom';
import {
    Button,
    ButtonGroup,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    UncontrolledButtonDropdown,
    UncontrolledDropdown,
} from 'reactstrap';
import { AccountStore } from '../../Account/AccountStore';
import { AppStore } from '../../Core/AppStore';
import { FavoriteButton } from '../../Core/FavoriteButton';
import { RdpLink } from '../../Core/RdpLink';
import { DisplayEditModal } from './Settings/DisplayEditModal';
import { DisplaysStore } from '../DisplaysStore';
import { TerminalModal } from '../TerminalModal';
import { AdvancedSettingsModal } from './Settings/AdvancedSettingsModal';
import { UnitsStore } from '../../Units/UnitsStore';
import { PartStatusModal } from '../../Parts/PartStatusModal';
import { PartsStore } from '../../Parts/PartsStore';
import { BsFillGearFill } from '@react-icons/all-files/bs/BsFillGearFill';

export interface IDisplayViewNavProps {
    readonly serialNumber: string;
}

@autoinject
@observer
export class DisplayViewNav extends React.Component<IDisplayViewNavProps> {
    public render() {
        const location = `/displays/${this.props.serialNumber}`;
        return (
            <SubTopBar hide={this.appStore.hiddenHeader}>
                <ul className="nav w-100 flex-items-center">
                    <li className="nav-item mx-2">
                        <LinkContainer exact to={location} className="nav-link stat-link fw-bold">
                            <Button color="link" className="nav-link fw-bold">
                                Status
                            </Button>
                        </LinkContainer>
                    </li>

                    <li className="nav-item mx-2">
                        <UncontrolledButtonDropdown>
                            <DropdownToggle caret color="link" className="fw-bold stat-link">
                                History
                            </DropdownToggle>
                            <DropdownMenu>
                                <DropdownItem tag={Link} to={`${location}/status-log`}>
                                    Status
                                </DropdownItem>
                                <DropdownItem tag={Link} to={`${location}/telemetry/chart`}>
                                    Performance
                                </DropdownItem>
                                <DropdownItem tag={Link} to={`${location}/event-log/list`}>
                                    Event Log
                                </DropdownItem>
                                <DropdownItem tag={Link} onClick={this.openPartStatusModal}>
                                    Part Status Log
                                </DropdownItem>
                                <DropdownItem tag={Link} to={`${location}/action-log`}>
                                    Display Action Log
                                </DropdownItem>
                                <DropdownItem tag={Link} to={`${location}/event-status-change-log`}>
                                    Event Status Change Log
                                </DropdownItem>
                                <DropdownItem tag={Link} to={`${location}/warranty-alert-log`}>
                                    Warranty Alert Log
                                </DropdownItem>
                                <DropdownItem tag={Link} to={`${location}/internal-alert-log`}>
                                    Internal Alert Log
                                </DropdownItem>
                                <DropdownItem tag={Link} to={`${location}/config-log`}>
                                    Config Log
                                </DropdownItem>
                                <DropdownItem tag={Link} to={`${location}/min-max`}>
                                    Min/Max
                                </DropdownItem>
                                <DropdownItem tag={Link} to={`${location}/live-charts`}>
                                    Live Charts
                                </DropdownItem>
                            </DropdownMenu>
                        </UncontrolledButtonDropdown>
                    </li>

                    <li className="nav-item mx-2">
                        <a
                            href={`${this.accountStore.portalUrl}/proxy/${this.props.serialNumber}`}
                            target={this.accountStore.user?.statusVuNewTab ? '_blank' : ''}
                            className={this.disabledClass + ' btn btn-link stat-link'}
                            data-selector={`display-status-vu-link`}
                        >
                            StatusVu&reg;
                        </a>
                    </li>

                    {this.display && (
                        <li className="nav-item mx-2">
                            <UncontrolledDropdown>
                                <DropdownToggle caret color="link" className="fw-bold stat-link">
                                    RDP
                                </DropdownToggle>
                                <DropdownMenu>
                                    <DropdownItem
                                        tag={RdpLink}
                                        type="chrome"
                                        display={this.display}
                                        displayLogin={this.displayLogin}
                                    >
                                        StatusVu&reg;
                                    </DropdownItem>
                                    <DropdownItem tag={RdpLink} type="input1" display={this.display}>
                                        VNC Player 1
                                    </DropdownItem>

                                    <DropdownItem tag={RdpLink} type="input2" display={this.display}>
                                        VNC Player 2
                                    </DropdownItem>

                                    <DropdownItem tag={RdpLink} type="camera" display={this.display}>
                                        Camera IP
                                    </DropdownItem>

                                    <DropdownItem tag={RdpLink} type="putty" display={this.display}>
                                        Terminal
                                    </DropdownItem>

                                    <DropdownItem tag={RdpLink} type="sigmaCenter" display={this.display}>
                                        InfiniteTouch®
                                    </DropdownItem>
                                </DropdownMenu>
                            </UncontrolledDropdown>
                        </li>
                    )}

                    <li className="nav-item mx-2">
                        <a className={this.disabledClass + ' btn btn-link stat-link'} onClick={this.openTerminalModal}>
                            Terminal
                        </a>
                    </li>

                    <li className="nav-item mx-2">
                        <a className="btn btn-link stat-link" onClick={this.openDisplayEditModal}>
                            Edit
                        </a>
                    </li>

                    <LinkContainer exact to={`${location}/settings`} className="nav-link stat-link fw-bold">
                        <Button color="link" className="nav-link fw-bold">
                            Settings
                        </Button>
                    </LinkContainer>

                    <li className="nav-item mx-2">
                        <Tooltip message="Advanced SmartVu® Settings">
                            <a className="btn btn-link stat-link" onClick={this.openAdvancedSettingsModal}>
                                <BsFillGearFill />
                            </a>
                        </Tooltip>
                    </li>

                    {this.display && (
                        <FavoriteButton className="align-self-center ms-auto me-3 f-24" display={this.display} />
                    )}
                    <SocketIcon
                        connected={this.displaysStore.connectionManager.connected}
                        className="justify-self-right me-3 f-24 float-end"
                        connectedText="Refreshing"
                    />

                    <HelpLink
                        className="justify-self-right p-1"
                        url="https://support.mri-inc.net/hc/en-us/articles/360002537771-Display-Status"
                    />

                    {this.unit && this.unit.displays.length > 1 && (
                        <li className="nav-item mx-2">
                            <ButtonGroup size="sm">
                                {this.unit.displays.map(d => (
                                    <LinkContainer exact to={`${d}`} key={d}>
                                        <Button disabled={d == this.display?.serialNumber} color="primary">
                                            {d}
                                        </Button>
                                    </LinkContainer>
                                ))}
                            </ButtonGroup>
                        </li>
                    )}
                </ul>
                <TerminalModal modalHandle={this.terminalModalHandle} serialNumber={this.props.serialNumber} />
                {this.display && <DisplayEditModal display={this.display} modalHandle={this.displayEditModalHandle} />}
                {this.display && (
                    <AdvancedSettingsModal display={this.display} modalHandle={this.advancedSettingsModalHandle} />
                )}
                {this.display && this.part && (
                    <PartStatusModal
                        modalHandle={this.statusModalHandle}
                        partStatus={this.part.partStatus}
                        partGuid={this.display.unitPartGuid ?? this.display.partGuid}
                        includeChildren
                    />
                )}
            </SubTopBar>
        );
    }

    @computed
    private get display() {
        return this.displaysStore.displays.get(this.props.serialNumber);
    }

    @computed
    private get disabledClass() {
        if (
            !this.display ||
            !this.display.displayConnectionData ||
            !this.display.displayConnectionData.displayBoardIP ||
            !this.display.displayConnectionData.nocServer
        ) {
            return 'disabled';
        } else return '';
    }

    @bind
    private openAdvancedSettingsModal(event: React.MouseEvent<HTMLAnchorElement>) {
        event.preventDefault();
        this.advancedSettingsModalHandle.open();
    }

    @bind
    private openTerminalModal(event: React.MouseEvent<HTMLAnchorElement>) {
        event.preventDefault();
        this.terminalModalHandle.open();
    }

    @bind
    private openDisplayEditModal(event: React.MouseEvent<HTMLAnchorElement>) {
        event.preventDefault();

        this.displayEditModalHandle.open();
    }

    @computed
    private get displayLogin() {
        return this.displaysStore.displayLogins.get(this.props.serialNumber);
    }

    @computed
    private get unit() {
        if (this.display) return this.unitsStore.units.get(this.display.unitSerialNumber);
    }

    @computed
    private get part() {
        if (this.display) {
            return this.partsStore.parts.get(this.display!.unitPartGuid ?? this.display!.partGuid);
        }
    }

    @bind
    private openPartStatusModal(event: React.MouseEvent<HTMLAnchorElement>) {
        event.preventDefault();
        this.statusModalHandle.open();
    }

    private statusModalHandle = new ModalHandle();
    private advancedSettingsModalHandle = new ModalHandle();
    private displayEditModalHandle = new ModalHandle();
    private terminalModalHandle = new ModalHandle();
    @inject
    private appStore!: AppStore;
    @inject
    private readonly accountStore!: AccountStore;
    @inject
    private readonly displaysStore!: DisplaysStore;
    @inject
    private readonly unitsStore!: UnitsStore;
    @inject
    private readonly partsStore!: PartsStore;
}
